import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from '../../shared/contexts/UserContext';
import { usePodMembership } from '../../shared/contexts';
import { buildFunctionUrl } from '../../shared/utils';
import { usdToCredits, creditsToUSD } from '../../shared/constants/billing';
import { trackSubscriptionEvent, trackEvent } from '../../shared/services/analytics';
import { capture } from '../../shared/services/posthog';

// =============================================================================
// PLAN CONFIGURATIONS - Subscription Tiers
// =============================================================================

// Canonical per-seat economics for the single 'pro' plan. Mirror of the
// marketing PricingPage and the backend subscription.js constants.
export const PRICE_PER_SEAT = 800;
export const CREDITS_PER_SEAT = 800;
export const MIN_SEATS = 1;
// Self-serve ceiling: Pro scales by card to 5 seats; 6+ (or a negotiated
// rate / invoicing) is the sales-assisted Practice path. Seat count is not the
// sales-touch trigger — negotiated terms are. Mirror of the backend constant.
export const MAX_SEATS = 5;

/**
 * Plan configurations - VERSION 1.0
 * One self-serve plan ('pro') priced per seat, plus the gated free tier.
 * Price and credits scale linearly with the seat count (seats x 800). The
 * `monthlyPrice` / `monthlyCredits` below are the 1-seat baseline; callers that
 * know the seat count derive the real figures from PRICE_PER_SEAT /
 * CREDITS_PER_SEAT.
 */
export const PLAN_CONFIGS = {
  free: {
    name: 'Free',
    monthlyPrice: 0,
    monthlyCredits: 0,
    storageLimit: 0,
    features: [
      'Project required for access'
    ]
  },
  pro: {
    name: 'Pro',
    perSeatPriceUSD: PRICE_PER_SEAT,
    creditsPerSeat: CREDITS_PER_SEAT,
    minSeats: MIN_SEATS,
    maxSeats: MAX_SEATS,
    // 1-seat baseline. Multiply by the seat count for the real figures.
    monthlyPrice: PRICE_PER_SEAT,
    monthlyCredits: CREDITS_PER_SEAT,
    storageLimit: -1,
    seats: 1,
    features: [
      '800 credits per seat per month, pooled across your team',
      '1 to 5 seats, billed monthly at $800 per seat',
      'Brand enforcement from your slide master',
      'Web research with citations',
      'SSO with Microsoft (Entra ID)',
      'Engagement code on receipts',
      'Booster pack top-ups'
    ]
  }
};

export const BOOSTER_PACKS = {
  // Frontend-visible booster-pack catalog — keep ONLY user-facing fields here.
  // Internal cost-basis values (API spend backing each credit) live in the
  // backend's BOOSTER_PACK_CONFIGS and must NOT be mirrored to the client,
  // since anything in this file ships in the JS bundle and lets prospects
  // back-solve our markup. Keys (`booster500` / `booster200`) MUST match the
  // backend keys — they're the lookup identifier passed to
  // createBoosterPackSession.
  booster200: {
    key: 'booster200',
    name: '200-credit booster',
    description: 'Adds 200 shared credits',
    retailPriceUSD: 200,
    creditCount: 200,
  },
  booster500: {
    key: 'booster500',
    name: '500-credit booster',
    description: 'Adds 500 shared credits',
    retailPriceUSD: 500,
    creditCount: 500,
  },
};

// Every legacy paid plan token now resolves to the single 'pro' plan.
const PRO_PLAN_TOKENS = new Set([
  'pro',
  'individual', 'solo', 'personal', 'analyst',
  'pod', 'case', 'casepod', 'dealteam', 'team'
]);

/**
 * Normalize plan key to 'pro' or 'free'
 *
 * NOTE: This function intentionally does NOT match on hardcoded Stripe price IDs
 * because test/live price IDs differ. The backend always stores normalized plan
 * keys ('pro', 'free') in Firestore, so price ID matching is unnecessary and
 * would cause misclassification if environments differ.
 */
export const normalizePlanKey = (plan) => {
  if (!plan) {
    return 'free';
  }

  if (typeof plan === 'object') {
    const possibleKeys = ['plan', 'key', 'tier', 'name', 'slug', 'id', 'value'];
    for (const candidate of possibleKeys) {
      if (plan[candidate]) {
        return normalizePlanKey(plan[candidate]);
      }
    }
    return 'free';
  }

  const raw = plan.toString().trim().toLowerCase();
  if (!raw) {
    return 'free';
  }

  const collapsedWhitespace = raw.replace(/[\s_-]+/g, ' ').trim();
  const tokens = collapsedWhitespace ? collapsedWhitespace.split(' ').filter(Boolean) : [];
  const alphaOnly = raw.replace(/[^a-z]/g, '');

  if (tokens.some((token) => PRO_PLAN_TOKENS.has(token))
      || alphaOnly === 'pro'
      || alphaOnly === 'individual'
      || alphaOnly === 'pod'
      || alphaOnly.includes('casepod')) {
    return 'pro';
  }

  // Fallback: if it looks like a price ID (starts with 'price_'), treat as paid.
  if (raw.startsWith('price_')) {
    return 'pro';
  }

  return 'free';
};

const normalizeTimestamp = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch (error) {
      console.warn('[useSubscription] Failed to normalize timestamp:', error);
      return null;
    }
  }
  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }
  return null;
};

const buildSubscriptionState = (subscriptionData = {}) => {
  const subData = subscriptionData || {};
  const status = subData.status || 'free';
  const normalizedPlan = normalizePlanKey(subData.plan) || 'free';

  const isCancelledButActive = status === 'cancelled' &&
    subData.cancelAtPeriodEnd &&
    subData.currentPeriodEnd &&
    new Date(subData.currentPeriodEnd) > new Date();

  const shouldUsePlan = status === 'active' || status === 'trialing' || isCancelledButActive;
  const resolvedPlan = shouldUsePlan ? normalizedPlan : 'free';
  const planConfig = PLAN_CONFIGS[resolvedPlan] || PLAN_CONFIGS.free;

  if (status === 'cancelled') {
    console.log('[useSubscription] Cancelled subscription detected:', {
      cancelAtPeriodEnd: subData.cancelAtPeriodEnd,
      currentPeriodEnd: subData.currentPeriodEnd,
      isStillActive: isCancelledButActive,
      now: new Date().toISOString()
    });
  }

  // New model: separate subscription and prepaid credits
  const rawSubscriptionCredits = Number(subData.subscriptionCredits);
  const rawPrepaidCredits = Number(subData.prepaidCredits);
  const rawLegacyBalance = Number(subData.creditBalance);
  const rawCreditUSD = Number(subData.creditBalanceUSD);

  const cycleStartsAtIso = normalizeTimestamp(subData.cycleStartsAt);
  const cycleEndsAtIso = normalizeTimestamp(subData.cycleEndsAt || subData.currentPeriodEnd);
  const currentPeriodEndIso = normalizeTimestamp(subData.currentPeriodEnd);
  const updatedAtIso = normalizeTimestamp(subData.updatedAt);
  const lastCreditDebitAtIso = normalizeTimestamp(subData.lastCreditDebitAt);

  // Resolve separate credit types
  let subscriptionCredits = Number.isFinite(rawSubscriptionCredits) ? rawSubscriptionCredits : 0;
  let prepaidCredits = Number.isFinite(rawPrepaidCredits) ? rawPrepaidCredits : 0;

  // Legacy fallback: if new fields not present, use legacy creditBalance
  if (!Number.isFinite(rawSubscriptionCredits) && !Number.isFinite(rawPrepaidCredits)) {
    if (Number.isFinite(rawLegacyBalance)) {
      subscriptionCredits = rawLegacyBalance;
      prepaidCredits = 0;
    } else if (Number.isFinite(rawCreditUSD)) {
      subscriptionCredits = usdToCredits(rawCreditUSD);
      prepaidCredits = 0;
    }
  }

  // Total credits = subscription + prepaid
  const totalCreditBalance = subscriptionCredits + prepaidCredits;
  const resolvedCreditUSD = creditsToUSD(totalCreditBalance);

  // Effective monthly allowance = seats x 800. The backend writes this resolved
  // figure to `cycleStartingCredits` at each grant/renewal (e.g. a 7-seat pod
  // has 5,600), so prefer it for the credit-bar denominator. Fall back to
  // seats x per-seat credits when it's missing (pre-grant state), or the plan
  // base for the free tier.
  const rawCycleStartingCredits = Number(subData.cycleStartingCredits);
  const podSeats = Math.max(1, Math.trunc(Number(subData.seats || subData.seatLimit) || 1));
  const seatAwareMonthlyCredits = (planConfig.creditsPerSeat || planConfig.monthlyCredits || 0) * podSeats;
  const effectiveMonthlyCredits = (shouldUsePlan && Number.isFinite(rawCycleStartingCredits) && rawCycleStartingCredits > 0)
    ? rawCycleStartingCredits
    : (shouldUsePlan ? seatAwareMonthlyCredits : planConfig.monthlyCredits);

  return {
    ...subData,
    status,
    plan: resolvedPlan,
    planConfig,
    monthlyCredits: effectiveMonthlyCredits,
    paygEnabled: subData.paygEnabled ?? false,
    // New separate fields
    subscriptionCredits: Math.max(0, subscriptionCredits),
    prepaidCredits: Math.max(0, prepaidCredits),
    // Combined total for display
    creditBalance: Math.max(0, totalCreditBalance),
    creditBalanceUSD: Math.max(0, resolvedCreditUSD),
    cycleStartsAt: cycleStartsAtIso,
    cycleEndsAt: cycleEndsAtIso,
    currentPeriodEnd: currentPeriodEndIso,
    updatedAt: updatedAtIso,
    lastCreditDebitAt: lastCreditDebitAtIso
  };
};

const sleep = (ms = 1000) => new Promise((resolve) => setTimeout(resolve, ms));

// =============================================================================
// HOOKS - useSubscription & useUsageStats
// =============================================================================

/**
 * useSubscription Hook (integrated from useSubscription.js)
 *
 * Optimized subscription status hook that reads directly from Firestore
 * instead of going through Cloud Functions, providing instant loading.
 */
function useSubscription() {
    const { pod, isLoading: isPodLoading } = usePodMembership();
    const subscription = useMemo(() => buildSubscriptionState(pod?.subscription || {}), [pod?.subscription]);

    return {
        subscription,
        isLoading: isPodLoading,
        error: null,
        refresh: async () => subscription,
        plan: subscription?.plan || 'free',
        status: subscription?.status || 'free',
        isActive: subscription?.status === 'active',
        isCancelled: subscription?.status === 'cancelled' || subscription?.cancelAtPeriodEnd,
        planConfig: subscription?.planConfig || PLAN_CONFIGS.free,
        monthlyCredits: subscription?.monthlyCredits ?? subscription?.planConfig?.monthlyCredits ?? PLAN_CONFIGS.free.monthlyCredits,
        paygEnabled: !!subscription?.paygEnabled,
        // Actual purchased seats (Stripe item quantity). The backend writes
        // subscription.seats; pod.seatLimit is the same value.
        seats: subscription?.seats || subscription?.seatLimit || (subscription?.plan === 'pro' ? 1 : 0),
        creditBalance: subscription?.creditBalance ?? 0,
        creditBalanceUSD: subscription?.creditBalanceUSD || 0,
        // Split balances surface separately to the UI (Monthly vs Add-on)
        subscriptionCredits: subscription?.subscriptionCredits ?? 0,
        prepaidCredits: subscription?.prepaidCredits ?? 0,
        cycleEndsAt: subscription?.cycleEndsAt || null,
        // Stripe-authoritative period end (raw value from webhook handlers).
        // Display this for the Renews date instead of cycleEndsAt — the
        // latter can drift off Stripe's billing anchor due to local math.
        currentPeriodEnd: subscription?.currentPeriodEnd || null
    };
}

// =============================================================================
// MAIN HOOK - useSettingsScreen
// =============================================================================

const useSettingsScreen = () => {
  const navigate = useNavigate();
  // URL ?code=XXX support: sales sends `re-stack.com/settings?code=SANDBOX`
  // and the redemption happens automatically on mount (or after sign-in).
  // Logic lives in a useEffect further down in this hook.
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, signOut, isAuthenticated } = useAuth();
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  const {
    pod,
    podId,
    podRole,
    hasPod,
    isAdmin,
    isMember,
    isLoading: isPodLoading,
  } = usePodMembership();
  const hasProcessedStripeCallback = useRef(false);

  // Helper function to get the appropriate function URL (pure — no deps)
  const getFunctionUrl = useCallback((functionName) => buildFunctionUrl(functionName), []);

  // State for settings

  // Use optimized hooks for subscription and usage
  const {
    subscription: subscriptionDetails,
    plan: subscriptionPlan,
    isLoading: isLoadingSubscription,
    isActive: isSubscriptionActive,
    isCancelled: isSubscriptionCancelled,
  } = useSubscription();

  const creditBalance = typeof subscriptionDetails?.creditBalance === "number"
    ? subscriptionDetails.creditBalance
    : 0;
  const subscriptionCreditsBalance = typeof subscriptionDetails?.subscriptionCredits === "number"
    ? subscriptionDetails.subscriptionCredits
    : 0;
  const prepaidCreditsBalance = typeof subscriptionDetails?.prepaidCredits === "number"
    ? subscriptionDetails.prepaidCredits
    : 0;

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat("en-US"),
    [],
  );

  const usageSummary = useMemo(() => {
    const planConfig = PLAN_CONFIGS[subscriptionPlan] || PLAN_CONFIGS.free;
    // Effective monthly credit cap = seats x 800. Prefer the backend-resolved
    // monthlyCredits (= cycleStartingCredits, written at each grant/renewal);
    // fall back to seats x per-seat credits.
    const seatCount = Math.max(1, Math.trunc(Number(subscriptionDetails?.seats) || 0));
    const baseCredits = subscriptionPlan === 'pro'
      ? (Number(subscriptionDetails?.monthlyCredits) || seatCount * (planConfig.creditsPerSeat || 0))
      : (planConfig.monthlyCredits || 0);
    const remaining = Math.max(0, creditBalance);
    const used = baseCredits > 0
      ? Math.max(0, baseCredits - Math.min(baseCredits, remaining))
      : 0;
    const progressValue = baseCredits > 0
      ? Math.min(100, (used / baseCredits) * 100)
      : 0;
    const creditsLabel = `${numberFormatter.format(Math.floor(remaining))} credits remaining`;
    const baseLabel = baseCredits > 0
      ? `${numberFormatter.format(baseCredits)} monthly credits`
      : 'Credit balance';
    const expiry = subscriptionDetails?.cycleEndsAt
      ? new Date(subscriptionDetails.cycleEndsAt)
      : null;
    const expiryLabel = expiry && !isNaN(expiry.getTime())
      ? expiry.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : null;
    const updatedSource = subscriptionDetails?.lastCreditDebitAt ||
      subscriptionDetails?.updatedAt ||
      null;
    const lastUpdatedDate = updatedSource ? new Date(updatedSource) : null;
    const lastUpdatedLabel = lastUpdatedDate && !isNaN(lastUpdatedDate.getTime())
      ? lastUpdatedDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : null;
    const lastUpdatedTooltip = lastUpdatedDate && !isNaN(lastUpdatedDate.getTime())
      ? lastUpdatedDate.toLocaleString()
      : null;

    return {
      baseCredits,
      creditsRemaining: remaining,
      creditsUsed: used,
      progressValue,
      creditsLabel,
      baseLabel,
      expiry,
      expiryLabel,
      lastUpdatedLabel,
      lastUpdatedTooltip,
      lowCredit: remaining <= 20,
      // Split balances for the UI: rolling subscription allowance vs
      // prepaid add-on credits from booster packs / trial coupons.
      subscriptionCreditsBalance,
      prepaidCreditsBalance
    };
  }, [
    subscriptionPlan,
    subscriptionDetails?.cycleEndsAt,
    subscriptionDetails?.lastCreditDebitAt,
    subscriptionDetails?.updatedAt,
    subscriptionDetails?.seats,
    subscriptionDetails?.monthlyCredits,
    creditBalance,
    subscriptionCreditsBalance,
    prepaidCreditsBalance,
    numberFormatter
  ]);

  const shouldSuggestCreditTopUp = usageSummary.lowCredit && normalizePlanKey(subscriptionPlan) === 'pro' && isAdmin;
  const hasOverage = false;

  // State for UI - VERSION 1.0
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Pod state
  const [podMembers, setPodMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [podNameInput, setPodNameInput] = useState('');
  // Project code / PO number — entered in-app by the pod admin (NOT collected
  // on Stripe Checkout). Was previously a custom_fields entry on the Stripe
  // session but rendered confusingly close to the postal code field.
  const [projectCodeInput, setProjectCodeInput] = useState('');

  // Coupon redemption state (shared across free-tier and paid users)
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null); // { success: boolean, message: string }

  // State for dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountGuidance, setDeleteAccountGuidance] = useState(null);
  // Organization deletion is a two step confirmation: the first dialog explains
  // what is destroyed and what is retained, the second requires the exact
  // organization name. The server requires the name too, so a mistyped or
  // remembered phrase cannot delete the wrong organization.
  const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false);
  const [deleteOrgConfirmOpen, setDeleteOrgConfirmOpen] = useState(false);
  const [confirmDeleteOrgText, setConfirmDeleteOrgText] = useState("");
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const [deleteOrgError, setDeleteOrgError] = useState(null);
  const [stripeRedirect, setStripeRedirect] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // No need for fetch functions - data comes from hooks in real-time!

  const handleBack = () => {
    navigate("/");
  };

  // Handle delete organization. Org Admin only; the server re-checks the role,
  // the exact name, that no other members remain, and that no paid subscription
  // is still live.
  const handleDeleteOrganization = async () => {
    if (isDeletingOrg) return;
    try {
      setIsDeletingOrg(true);
      setDeleteOrgError(null);
      const response = await fetch(getFunctionUrl("deleteOrganization"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ confirmation: confirmDeleteOrgText }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setIsDeletingOrg(false);
        setDeleteOrgError(
          body.code === "organization_has_members"
            ? "Remove the other members before deleting this organization."
            : body.code === "organization_subscription_active"
              ? "Cancel the subscription before deleting this organization."
              : body.code === "organization_delete_confirmation_mismatch"
                ? "That does not match the organization name."
                : body.error || body.message || "Failed to delete the organization."
        );
        return;
      }
      await response.json().catch(() => ({}));
      setDeleteOrgConfirmOpen(false);
      setDeleteOrgDialogOpen(false);
      setConfirmDeleteOrgText("");
      setIsDeletingOrg(false);
      showNotification(
        "Organization deleted. Your projects were kept and unlinked.",
        "success"
      );
      // The whole console reads from the org record, so reload rather than try
      // to reconcile every dependent view by hand.
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      console.error("Error deleting organization:", error);
      setIsDeletingOrg(false);
      setDeleteOrgError("Failed to delete the organization. Please try again.");
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (isDeletingAccount) return; // Prevent multiple requests

    try {
      setIsDeletingAccount(true);
      setDeleteAccountGuidance(null);
      showNotification("Processing account deletion...", "info");

      // Call the backend to delete the account and all associated data
      const response = await fetch(getFunctionUrl("deleteUserAccount"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          confirmText: confirmDeleteText,
        }),
      });

      if (response.ok) {
        await response.json();
        setDeleteDialogOpen(false);
        showNotification(
          "Account deletion completed. You will be signed out shortly.",
          "success",
        );

        // Sign out and redirect after successful deletion
        setTimeout(() => {
          signOut();
          navigate("/");
        }, 3000);
      } else {
        const error = await response.json().catch(() => ({}));
        setIsDeletingAccount(false);
        const requestError = new Error(error.message || "Failed to delete account");
        requestError.code = error.code || null;
        requestError.blockers = Array.isArray(error.blockers) ? error.blockers : [];
        throw requestError;
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      let errorMessage = "Failed to delete account. Please try again.";
      let guidance = null;

      if (error.code === "ACCOUNT_DELETION_SUPPORT_REQUIRED") {
        guidance = {
          severity: "warning",
          title: "Contact support",
          message: error.message || "Contact hi@re-stack.com to complete account deletion safely.",
        };
        errorMessage = guidance.message;
      } else if (error.code === "RECENT_AUTH_REQUIRED") {
        guidance = {
          severity: "warning",
          title: "Sign in again",
          message: "For security, account deletion requires a recent sign-in. Close this dialog, sign out, sign back in, and retry within 10 minutes.",
        };
        errorMessage = guidance.message;
      } else if (error.code === "ACCOUNT_TRANSFER_REQUIRED") {
        const names = (error.blockers || [])
          .map((blocker) => blocker.organizationName || blocker.projectName)
          .filter(Boolean);
        const affected = names.length > 0
          ? ` Affected: ${[...new Set(names)].join(", ")}.`
          : "";
        guidance = {
          severity: "warning",
          title: "Transfer ownership first",
          message: `${error.message}${affected} Transfer the listed administrator and billing responsibilities, then retry.`,
        };
        errorMessage = guidance.message;
      } else if (error.code === "ACCOUNT_DELETION_INCOMPLETE"
        || error.code === "ACCOUNT_SESSION_REVOCATION_FAILED") {
        guidance = {
          severity: "error",
          title: "Deletion did not finish",
          message: error.message || "Your organization access may already be removed, and you may need to sign in again before retrying or contacting support.",
        };
        errorMessage = guidance.message;
      } else if (error.message.includes("fetch")) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.message) {
        errorMessage = `Failed to delete account: ${error.message}`;
      }

      setDeleteAccountGuidance(guidance);
      showNotification(errorMessage, "error");
      setIsDeletingAccount(false);
      // Re-open dialog so user can try again if it was closed
      if (!deleteDialogOpen) {
        setDeleteDialogOpen(true);
      }
    }
  };

  // Notification function
  const showNotification = useCallback((message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  // Auto-claim trial credits for users without a pod
  const hasClaimedTrial = useRef(false);
  useEffect(() => {
    if (isDeletingAccount || isPodLoading || hasPod || !currentUser || hasClaimedTrial.current) return;
    hasClaimedTrial.current = true;

    (async () => {
      try {
        const response = await fetch(buildFunctionUrl('claimTrialCredits'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
          },
        });
        if (response.ok) {
          const result = await response.json();
          if (!result.alreadyClaimed) {
            console.log('[Settings] Trial credits claimed:', result.credits);
          }
        }
      } catch (error) {
        console.warn('[Settings] Failed to claim trial credits:', error.message);
      }
    })();
  }, [isDeletingAccount, isPodLoading, hasPod, currentUser]);

  useEffect(() => {
    if (pod?.name) {
      setPodNameInput(pod.name);
      return;
    }
    if (!hasPod) {
      setPodNameInput('');
    }
  }, [pod?.name, hasPod]);

  // Mirror project code from the pod doc so the input is pre-filled when
  // an admin re-opens Settings and so it can be edited inline post-subscribe.
  useEffect(() => {
    if (pod?.projectCode) {
      setProjectCodeInput(pod.projectCode);
      return;
    }
    if (!hasPod) {
      setProjectCodeInput('');
    }
  }, [pod?.projectCode, hasPod]);

  // Seat-change confirmation dialog state. Admin clicks "+ Seat" or
  // "− Seat", we fetch a Stripe preview (prorated $ amount), show a dialog,
  // then commit on confirm. Rugpull-prevention: no surprise charges.
  //
  // Shape of seatChangePreview: { delta, currentSeats, newSeats,
  //   monthlyDeltaUSD, newMonthlyUSD, newMonthlyCredits,
  //   proratedAmountDueUSD, nextInvoiceTotalUSD, fullSeatPriceUSD,
  //   currency, cycleEndsAt, kind }
  const [seatChangePreview, setSeatChangePreview] = useState(null);
  const [loadingSeatChangePreview, setLoadingSeatChangePreview] = useState(false);
  const [confirmingSeatChange, setConfirmingSeatChange] = useState(false);
  const seatCountForChanges = Math.max(
    0,
    Math.trunc(Number(subscriptionDetails?.seats || subscriptionDetails?.seatLimit) || 0)
  );

  // ─── Seat-change flow ────────────────────────────────────────────────
  // requestSeatChange(delta) → preview → cancel/confirm → performSeatChange
  // delta: +1 to add a seat, -1 to remove. Backend enforces admin-only
  // and refuses removals that would leave existing members without a seat.
  const requestSeatChange = useCallback(async (delta) => {
    if (!currentUser) {
      showNotification('Please sign in to continue.', 'warning');
      return;
    }
    const numericDelta = Math.trunc(Number(delta));
    if (!Number.isFinite(numericDelta) || numericDelta === 0) return;
    const targetSeats = seatCountForChanges + numericDelta;

    setLoadingSeatChangePreview(true);
    try {
      const response = await fetch(getFunctionUrl('previewPodSeatChange'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ targetSeats }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Could not preview seat change (${response.status})`);
      }
      const hasLockedQuote = Number.isSafeInteger(data?.quoteAmountMinor)
        && typeof data?.quoteCurrency === 'string'
        && Number.isSafeInteger(data?.quoteProrationDate);
      if (data?.delta > 0 && (
        data?.proratedAmountDueUSD == null
        || data?.exactAmountAvailable === false
        || !hasLockedQuote
      )) {
        throw new Error('An exact charge preview is unavailable. No seat change was made. Please try again.');
      }
      if (data?.delta !== 0 && typeof data?.operationId !== 'string') {
        throw new Error('A safe seat operation could not be prepared. No seat change was made. Please try again.');
      }
      setSeatChangePreview(data);
    } catch (error) {
      console.error('Seat change preview error:', error);
      showNotification(error.message || 'Could not preview seat change.', 'error');
    } finally {
      setLoadingSeatChangePreview(false);
    }
  }, [currentUser, getFunctionUrl, seatCountForChanges, showNotification]);

  const cancelSeatChange = useCallback(() => {
    setSeatChangePreview(null);
  }, []);

  const performSeatChange = useCallback(async () => {
    if (!seatChangePreview || !currentUser) return;
    if (
      seatChangePreview.delta > 0
      && (
        seatChangePreview.proratedAmountDueUSD == null
        || seatChangePreview.exactAmountAvailable === false
        || !Number.isSafeInteger(seatChangePreview.quoteAmountMinor)
        || typeof seatChangePreview.quoteCurrency !== 'string'
        || !Number.isSafeInteger(seatChangePreview.quoteProrationDate)
      )
    ) {
      showNotification('An exact charge preview is required before adding a seat. Refresh and try again.', 'error');
      return;
    }
    if (typeof seatChangePreview.operationId !== 'string') {
      showNotification('A safe seat operation identifier is required. Refresh and try again.', 'error');
      return;
    }
    const delta = seatChangePreview.delta;
    setConfirmingSeatChange(true);
    try {
      const response = await fetch(getFunctionUrl('adjustPodSeats'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          targetSeats: seatChangePreview.targetSeats ?? seatChangePreview.newSeats,
          expectedCurrentSeats: seatChangePreview.expectedCurrentSeats ?? seatChangePreview.currentSeats,
          expectedOperationId: seatChangePreview.operationId,
          expectedQuoteAmountMinor: seatChangePreview.quoteAmountMinor,
          expectedQuoteCurrency: seatChangePreview.quoteCurrency,
          expectedQuoteProrationDate: seatChangePreview.quoteProrationDate,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Seat change failed (${response.status})`);
      }
      // The backend updates the subscription item quantity and returns the new
      // seat count. An increase grants prorated credits now. A decrease has no
      // mid-cycle refund; the lower rate applies to the next invoice and future
      // monthly credit grants use the new seat count.
      const newSeats = data.seats ?? data.seatLimit;
      const n = Math.abs(delta);
      if (delta > 0) {
        showNotification(
          `${n} seat${n === 1 ? '' : 's'} added. Your plan now has ${newSeats} seat${newSeats === 1 ? '' : 's'}.`,
          'success'
        );
      } else {
        const recurringDelta = Number(seatChangePreview.monthlyDeltaUSD || 0);
        const nextRecurring = Number(seatChangePreview.newMonthlyUSD || 0);
        const recurringSummary = recurringDelta < 0
          ? `your recurring amount decreases by $${Math.abs(recurringDelta).toLocaleString()}/mo to $${nextRecurring.toLocaleString()}/mo at renewal`
          : recurringDelta > 0
            ? `your recurring amount increases by $${recurringDelta.toLocaleString()}/mo to $${nextRecurring.toLocaleString()}/mo at renewal`
            : `your recurring amount remains $${nextRecurring.toLocaleString()}/mo at renewal`;
        showNotification(
          `${n} seat${n === 1 ? '' : 's'} removed. Your plan now has ${newSeats} seat${newSeats === 1 ? '' : 's'}; ${recurringSummary}.`,
          'success'
        );
      }
      setSeatChangePreview(null);
    } catch (error) {
      console.error('Seat change error:', error);
      showNotification(error.message || 'Could not update seats.', 'error');
    } finally {
      setConfirmingSeatChange(false);
    }
  }, [seatChangePreview, currentUser, getFunctionUrl, showNotification]);

  // Auto-detected pending invites — populated when a signed-in user without
  // a pod has been invited to one or more pods (admin used invitePodMember
  // with their email). PRIMARY discovery path; doesn't require email
  // delivery to actually work. Refreshed on Settings load and after an
  // accept/decline action.
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingInvitesLoading, setPendingInvitesLoading] = useState(false);
  const [acceptingInviteFor, setAcceptingInviteFor] = useState(null);
  const [decliningInviteFor, setDecliningInviteFor] = useState(null);
  // Org-level join options (firm seat invites + domain claim). Separate from
  // pod invites: the org is the license/billing layer, the pod is the
  // engagement workspace.
  const [orgJoinOptions, setOrgJoinOptions] = useState({ invites: [], domainOrg: null, emailVerified: true });
  const [joiningOrgId, setJoiningOrgId] = useState(null);
  const [orgJoinNeedsVerification, setOrgJoinNeedsVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);

  const refreshPendingInvites = useCallback(async () => {
    // Only suppress the lookup if the user is on a PAID pod — free-pod
    // users (auto-created on signup) should still see invites for paid
    // pods they've been added to. The backend's listPendingInvitesForUser
    // also short-circuits if the user has a paid podId.
    const onPaidPod = hasPod && pod?.subscription?.plan && pod.subscription.plan !== 'free';
    if (!currentUser || onPaidPod) {
      setPendingInvites([]);
      return;
    }
    setPendingInvitesLoading(true);
    try {
      const response = await fetch(getFunctionUrl('listPendingInvites'), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
      });
      if (!response.ok) {
        // Soft-fail — invites are a discovery aid; the manual code input still works.
        console.warn('[Settings] listPendingInvites failed:', response.status);
        setPendingInvites([]);
        return;
      }
      const data = await response.json();
      setPendingInvites(Array.isArray(data?.invites) ? data.invites : []);
    } catch (err) {
      console.warn('[Settings] listPendingInvites error:', err.message);
      setPendingInvites([]);
    } finally {
      setPendingInvitesLoading(false);
    }
  }, [currentUser, hasPod, pod?.subscription?.plan, getFunctionUrl]);

  useEffect(() => {
    refreshPendingInvites();
  }, [refreshPendingInvites]);

  const handleAcceptPendingInvite = async (podId) => {
    if (!currentUser || !podId) return;
    setAcceptingInviteFor(podId);
    try {
      const response = await fetch(getFunctionUrl('acceptPodInvite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ podId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Could not accept invite.');
      }
      showNotification('Joined the project successfully.', 'success');
      // Force a fresh user/pod read by reloading; the contexts re-fetch on mount.
      if (typeof window !== 'undefined') {
        window.location.href = `${window.location.pathname}?invite_accepted=1`;
      }
    } catch (err) {
      console.error('[Settings] acceptPendingInvite failed:', err);
      showNotification(err.message || 'Could not accept invite.', 'error');
      // Re-pull the list — the invite may have been revoked by admin.
      refreshPendingInvites();
    } finally {
      setAcceptingInviteFor(null);
    }
  };

  const handleDeclinePendingInvite = async (podId) => {
    if (!currentUser || !podId) return;
    setDecliningInviteFor(podId);
    try {
      const response = await fetch(getFunctionUrl('declinePodInvite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ podId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Could not decline invite.');
      }
      showNotification('Invite declined.', 'info');
      refreshPendingInvites();
    } catch (err) {
      console.error('[Settings] declinePendingInvite failed:', err);
      showNotification(err.message || 'Could not decline invite.', 'error');
    } finally {
      setDecliningInviteFor(null);
    }
  };

  // Org seat discovery: invites pending for this email + a domain-claimable
  // org. The backend matches on the AUTH TOKEN email and returns nothing for
  // users already in an org.
  const refreshOrgJoinOptions = useCallback(async () => {
    if (!currentUser) {
      setOrgJoinOptions({ invites: [], domainOrg: null, emailVerified: true });
      return;
    }
    try {
      const response = await fetch(getFunctionUrl('listOrgJoinOptions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        console.warn('[Settings] listOrgJoinOptions failed:', response.status);
        return;
      }
      const data = await response.json();
      setOrgJoinOptions({
        invites: Array.isArray(data?.invites) ? data.invites : [],
        domainOrg: data?.domainOrg || null,
        emailVerified: data?.emailVerified !== false,
      });
    } catch (err) {
      console.warn('[Settings] listOrgJoinOptions error:', err.message);
    }
  }, [currentUser, getFunctionUrl]);

  useEffect(() => {
    refreshOrgJoinOptions();
  }, [refreshOrgJoinOptions]);

  // Accept an org seat (invite or domain claim). Both routes land on the same
  // backend accept, which requires a VERIFIED token email; surface that state
  // with a resend option instead of a dead-end error.
  const handleJoinOrg = async (orgId, { viaDomain = false } = {}) => {
    if (!currentUser || !orgId) return;
    setJoiningOrgId(orgId);
    try {
      const endpoint = viaDomain ? 'claimOrgSeatByDomain' : 'acceptOrgInvite';
      const response = await fetch(getFunctionUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ orgId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 403 && /verify/i.test(data?.error || '')) {
          setOrgJoinNeedsVerification(true);
          showNotification('Verify your email address first, then accept the invite again.', 'warning');
          return;
        }
        throw new Error(data?.error || 'Could not join the organization.');
      }
      showNotification('Seat claimed. Welcome aboard!', 'success');
      // Hard reload so the contexts pick up orgId/orgRoles fresh (same pattern
      // as the pod invite accept).
      if (typeof window !== 'undefined') {
        window.location.href = `${window.location.pathname}?org_joined=1`;
      }
    } catch (err) {
      console.error('[Settings] joinOrg failed:', err);
      showNotification(err.message || 'Could not join the organization.', 'error');
      refreshOrgJoinOptions();
    } finally {
      setJoiningOrgId(null);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const { resendVerificationEmail } = await import('../../shared/services/firebase');
      await resendVerificationEmail();
      showNotification('Verification email sent. Check your inbox, then accept the invite again.', 'success');
    } catch (err) {
      showNotification(err.message || 'Could not send the verification email.', 'error');
    } finally {
      setResendingVerification(false);
    }
  };

  const loadPodMembers = useCallback(async () => {
    if (!hasPod || !podId || !currentUserRef.current) {
      setPodMembers((prev) => prev.length === 0 ? prev : []);
      return;
    }

    try {
      setIsLoadingMembers(true);
      const response = await fetch(buildFunctionUrl('listPodMembers'), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await currentUserRef.current.getIdToken()}`
        }
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to load pod members');
      }

      const payload = await response.json();
      setPodMembers(Array.isArray(payload.members) ? payload.members : []);
    } catch (error) {
      console.error('Failed to load pod members:', error);
      setPodMembers([]);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [hasPod, podId]);

  useEffect(() => {
    loadPodMembers();
    // Re-fetch whenever the pod's member roster changes (someone leaves,
    // gets removed, or is deleted via cross-pod cleanup). The pod doc's
    // onSnapshot updates `pod.memberCount` immediately on any membership
    // change, so this dependency is what makes the admin tracker reactive
    // — without it, the admin would have to refresh the page to see a
    // member's "Leave" action propagate. Same for pendingInvites length
    // changes (cancel-invite, accept-invite, decline-invite).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPodMembers, pod?.memberCount, pod?.pendingInvites?.length]);

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const [buyingBoosterPack, setBuyingBoosterPack] = useState(null);

  const handleBuyBoosterPack = async (packKey) => {
    if (!currentUser) {
      showNotification("Please sign in to purchase boosters.", "error");
      return;
    }
    if (!isAdmin) {
      showNotification("Only project admins can buy boosters.", "warning");
      return;
    }
    if (!packKey || buyingBoosterPack) {
      return;
    }

    const packConfig = BOOSTER_PACKS[packKey];

    // Track credit purchase initiation
    trackEvent('booster_purchase_initiate', {
      event_category: 'Subscription',
      pack_key: packKey,
      credit_count: packConfig?.creditCount || 0,
      price: packConfig?.retailPriceUSD || 0
    });

    try {
      setBuyingBoosterPack(packKey);
      showNotification("Redirecting to secure checkout...", "info");

      const response = await fetch(getFunctionUrl("createBoosterPackSession"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          packKey,
          successUrl: `${window.location.origin}/settings?booster=success&booster_session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/settings?booster=cancelled`,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || "Failed to create booster checkout session");
      }

      const session = await response.json();
      if (!session?.url) {
        throw new Error("No checkout URL returned from server");
      }

      window.location.href = session.url;
    } catch (error) {
      console.error("Error purchasing booster pack:", error);

      // Track credit purchase failure
      trackEvent('booster_purchase_failed', {
        event_category: 'Subscription',
        pack_key: packKey,
        error_message: error.message
      });

      showNotification(
        error.message
          ? `Failed to start booster purchase: ${error.message}`
          : "Failed to start booster purchase. Please try again.",
        "error",
      );
    } finally {
      setBuyingBoosterPack(null);
    }
  };

  // Save project code for the current pod (admin only). The field used to
  // be collected on the Stripe Checkout custom_fields, which rendered
  // confusingly close to the postal-code input. Now it lives in-app and
  // is editable any time post-subscribe.
  const saveProjectCode = async (rawValue) => {
    if (!currentUser) {
      showNotification("Please sign in to continue.", "warning");
      return;
    }
    const value = (rawValue || '').trim();
    try {
      const response = await fetch(getFunctionUrl("updatePodMetadata"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ projectCode: value || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Failed to save project code (${response.status})`);
      }
      showNotification(
        value ? "Project code saved." : "Project code cleared.",
        "success"
      );
    } catch (error) {
      console.error("Failed to save project code:", error);
      showNotification(error.message || "Could not save project code.", "error");
    }
  };

  // Start a new subscription via Stripe Checkout for the chosen seat count
  // (1 to 5). There is a single plan ('pro'); the seat count sets the
  // subscription item quantity (seats x $800/mo, seats x 800 credits). Existing
  // paid customers change their seat count through the seat stepper
  // (requestSeatChange/performSeatChange), not this function.
  const handleSubscribe = async (seats = 1) => {
    if (isProcessingPayment) return;

    if (!currentUser) {
      showNotification("Please sign in to continue.", "warning");
      return;
    }

    const seatCount = Math.min(MAX_SEATS, Math.max(MIN_SEATS, Math.trunc(Number(seats) || 1)));

    // Team name is optional — defaults to user's name + "'s Team" on the backend

    // Track subscription initiation
    trackSubscriptionEvent('initiate', {
      plan_type: 'pro',
      seats: seatCount,
      current_plan: subscriptionPlan,
      price: seatCount * PRICE_PER_SEAT
    });
    capture('subscription_initiated', {
      plan: 'pro',
      seats: seatCount,
      current_plan: subscriptionPlan,
      price: seatCount * PRICE_PER_SEAT,
    });

    try {
      setIsProcessingPayment(true);
      showNotification("Redirecting to secure payment...", "info");
      setStripeRedirect(true);

      // Create a Stripe Checkout session for the chosen seat count
      console.log("🔍 Creating checkout session:", {
        plan: 'pro',
        seats: seatCount,
        functionUrl: getFunctionUrl("createCheckoutSession"),
      });

      const response = await fetch(getFunctionUrl("createCheckoutSession"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          plan: 'pro',
          seats: seatCount,
          billingCycle: "monthly", // Only monthly billing
          podName: podNameInput.trim() || null,
          projectCode: projectCodeInput.trim() || null,
          successUrl: `${window.location.origin}/settings?payment=success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/settings?payment=cancelled`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Checkout session error:", response.status, errorText);
        throw new Error(
          `Failed to create checkout session: ${errorText || response.statusText}`,
        );
      }

      const session = await response.json();
      console.log("📍 Checkout session created:", {
        sessionId: session.id,
        sessionUrl: session.url,
        livemode: session.livemode,
        isLocalhost: window.location.hostname === "localhost",
        fullResponse: session,
      });

      // For debugging - alert the URL before redirect
      const debugUrl =
        session.url || `https://checkout.stripe.com/c/pay/${session.id}`;
      console.log("📍 About to redirect to:", debugUrl);

      // Use the URL from the backend response if available
      if (session.url) {
        console.log("📍 Using backend-provided URL:", session.url);
        console.log("📍 Session details:", {
          id: session.id,
          livemode: session.livemode,
          url: session.url,
        });

        // Direct redirect to Stripe checkout
        window.location.href = session.url;
      } else if (session.id) {
        // This shouldn't happen if backend is working correctly
        console.error(
          "⚠️ No URL provided by backend, only session ID:",
          session.id,
        );
        throw new Error("Backend did not provide checkout URL");
      } else {
        throw new Error("No session URL or ID returned from server");
      }
    } catch (error) {
      console.error("Payment error:", error);

      // Track payment failure
      trackSubscriptionEvent('payment_failed', {
        plan_type: 'pro',
        error_message: error.message,
        stage: 'checkout_creation'
      });

      showNotification(`Payment error: ${error.message}`, "error");
      setIsProcessingPayment(false);
      setStripeRedirect(false);
    }
  };

  // Handle URL parameters for payment callbacks with session finalization
  useEffect(() => {
    if (hasProcessedStripeCallback.current) {
      return;
    }

    if (!isAuthenticated || !currentUser) {
      return;
    }

    const clearCallbackParams = (keys) => {
      const params = new URLSearchParams(window.location.search);
      let mutated = false;
      keys.forEach((key) => {
        if (params.has(key)) {
          params.delete(key);
          mutated = true;
        }
      });
      if (!mutated) {
        return;
      }
      const nextSearch = params.toString();
      const nextUrl = nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname;
      window.history.replaceState({}, document.title, nextUrl);
    };

    const finalizeWithPolling = async ({ sessionId, endpoint, label }) => {
      const token = await currentUser.getIdToken();
      // Increased from 6 to 10 attempts to handle slow Stripe webhooks
      // Total wait time: ~35 seconds (2s + 3s + 4s + 5s + 5s + 5s + 5s + 5s + 5s + 5s)
      const maxAttempts = 10;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const response = await fetch(buildFunctionUrl(endpoint), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sessionId }),
        });

        let payload = {};
        try {
          payload = await response.json();
        } catch (parseError) {
          payload = {};
        }

        if (!response.ok) {
          throw new Error(payload.error || `Failed to finalize ${label}`);
        }

        if (payload?.success) {
          return payload;
        }

        if (payload?.reason === "payment_not_settled") {
          console.log(`[Settings] ${label} finalize pending (attempt ${attempt}/${maxAttempts}, status: ${payload?.status || "unknown"})`);
          if (attempt === maxAttempts) {
            // More helpful error message with clear next steps
            throw new Error(
              "Payment received but sync is taking longer than expected. " +
              "Your subscription will activate automatically within a minute. " +
              "Please refresh this page shortly."
            );
          }
          // Exponential backoff: 2s, 3s, 4s, 5s, 5s... (capped at 5s)
          await sleep(Math.min(1000 + 1000 * attempt, 5000));
          continue;
        }

        throw new Error(payload?.error || `Failed to finalize ${label}`);
      }

      return null;
    };

    const processCallbacks = async () => {
      setStripeRedirect(false);
      setIsProcessingPayment(false);

      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get("payment");
      const paymentSessionId = urlParams.get("session_id");
      const boosterStatus = urlParams.get("booster");
      const boosterSessionId = urlParams.get("booster_session_id");

      // Clear URL params immediately to prevent re-triggers on failure
      if (paymentStatus || boosterStatus) {
        clearCallbackParams(["payment", "session_id", "booster", "booster_session_id"]);
      }

      if (paymentStatus === "success") {
        if (paymentSessionId) {
          showNotification("Payment successful! Finalizing subscription...", "info");
          try {
            await finalizeWithPolling({
              sessionId: paymentSessionId,
              endpoint: "finalizeCheckoutSession",
              label: "subscription",
            });

            // Track successful subscription. Price is derived post-finalize
            // from the pod's seat count once subscription state refreshes.
            capture('subscription_completed', { plan: 'pro' });
            trackSubscriptionEvent('subscribe', {
              plan_type: 'pro',
              currency: 'USD'
            });

            showNotification("Subscription updated successfully!", "success");

            // The buyer is now their org's admin. Land them in the Admin panel
            // with the first-run checklist (invite team, deploy add-in, first
            // deck) instead of leaving them on a quiet Settings page.
            if (typeof window !== 'undefined') {
              window.location.href = '/settings?welcome=1';
              return;
            }
          } catch (error) {
            console.error("Failed to finalize subscription checkout:", error);

            // Track finalization failure
            trackSubscriptionEvent('payment_failed', {
              plan_type: 'pro',
              error_message: error.message,
              stage: 'finalization'
            });

            showNotification(
              error.message || "Payment captured but subscription sync failed. Please refresh.",
              "warning",
            );
          }
        } else {
          showNotification("Payment completed but session reference was missing. Please refresh.", "warning");
        }
      } else if (paymentStatus === "cancelled") {
        // Track cancelled payment
        trackSubscriptionEvent('cancel', {
          plan_type: 'pro',
          stage: 'checkout'
        });

        showNotification("Payment was cancelled.", "warning");
      }

      if (boosterStatus === "success") {
        if (boosterSessionId) {
          showNotification("Booster purchase successful! Finalizing balance...", "info");
          try {
            await finalizeWithPolling({
              sessionId: boosterSessionId,
              endpoint: "finalizeBoosterPackSession",
              label: "booster purchase",
            });

            trackEvent('booster_purchase_success', {
              event_category: 'Subscription',
              session_id: boosterSessionId
            });

            showNotification("Booster credits added successfully!", "success");
          } catch (error) {
            console.error("Failed to finalize booster pack checkout:", error);

            trackEvent('booster_purchase_failed', {
              event_category: 'Subscription',
              error_message: error.message,
              stage: 'finalization'
            });

            showNotification(
              error.message || "Purchase completed but boosters are still syncing. Please refresh shortly.",
              "warning",
            );
          }
        } else {
          showNotification("Booster purchase completed but session reference was missing. Please refresh.", "warning");
        }
      } else if (boosterStatus === "cancelled") {
        trackEvent('booster_purchase_cancelled', {
          event_category: 'Subscription'
        });

        showNotification("Booster purchase was cancelled.", "warning");
      }
    };

    processCallbacks().finally(() => {
      hasProcessedStripeCallback.current = true;
    });
  }, [currentUser, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle return from billing portal - SIMPLIFIED
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("from") === "portal") {
      // Data auto-refreshes via real-time listeners
      showNotification("Subscription details updated", "info");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [showNotification]);

  // Handle subscription management (billing portal) - SIMPLIFIED
  const handleManageSubscription = async () => {
    try {
      if (!isAdmin) {
        showNotification("Only project admins can manage billing.", "warning");
        return;
      }

      showNotification("Opening billing portal...", "info");

      const response = await fetch(getFunctionUrl("createPortalSession"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          returnUrl: `${window.location.origin}/settings?from=portal`,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create portal session");
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error("Error managing subscription:", error);
      showNotification("Failed to open subscription management", "error");
    }
  };

  const handleCancelSubscriptionNow = async () => {
    if (!isAdmin || isCancellingSubscription) return;
    const confirmed = window.confirm(
      "Cancel your Restack subscription now? Access ends immediately. If eligible, your 30-day money-back guarantee or prorated refund is processed automatically."
    );
    if (!confirmed) return;

    setIsCancellingSubscription(true);
    try {
      const response = await fetch(getFunctionUrl("cancelSubscription"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ immediateRefund: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Cancellation failed");
      showNotification("Subscription cancelled. Any eligible refund is processing.", "success");
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      showNotification(error.message || "Failed to cancel subscription", "error");
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleInviteMember = async (email) => {
    if (!email || !podId) {
      showNotification('Please enter an email to invite.', 'warning');
      return;
    }
    if (!isAdmin) {
      showNotification('Only project admins can invite members.', 'warning');
      return;
    }

    try {
      const response = await fetch(getFunctionUrl('invitePodMember'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          podId,
          memberEmail: email.trim(),
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to invite member');
      }

      setInviteEmail('');
      showNotification('Invite sent.', 'success');
      loadPodMembers();
    } catch (error) {
      console.error('Invite failed:', error);
      showNotification(error.message || 'Failed to invite member', 'error');
    }
  };

  // Admin-side: cancel a pending invite by email. Different from
  // declinePodInvite (which is the invitee-side action) — this lets the
  // admin un-invite someone before they accept. The pod doc onSnapshot
  // (via podHooks) propagates the change automatically; no manual refetch.
  const [cancellingInviteFor, setCancellingInviteFor] = useState(null);
  const handleCancelPendingInvite = async (memberEmail) => {
    if (!podId || !memberEmail) return;
    if (!isAdmin) {
      showNotification('Only project admins can cancel invites.', 'warning');
      return;
    }
    setCancellingInviteFor(memberEmail);
    try {
      const response = await fetch(getFunctionUrl('cancelPendingInvite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ podId, memberEmail }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to cancel invite');
      }
      showNotification(`Invite to ${memberEmail} cancelled.`, 'success');
    } catch (err) {
      console.error('Cancel invite failed:', err);
      showNotification(err.message || 'Failed to cancel invite', 'error');
    } finally {
      setCancellingInviteFor(null);
    }
  };

  const handleRemoveMember = async (targetUserId) => {
    if (!podId || !targetUserId) {
      return;
    }
    if (!isAdmin && targetUserId !== currentUser?.uid) {
      showNotification('Only project admins can remove members.', 'warning');
      return;
    }

    try {
      const response = await fetch(getFunctionUrl('removePodMember'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          podId,
          targetUserId,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to remove member');
      }

      showNotification('Member removed.', 'success');
      loadPodMembers();
    } catch (error) {
      console.error('Remove member failed:', error);
      showNotification(error.message || 'Failed to remove member', 'error');
    }
  };

  const handleCopyInviteLink = async () => {
    if (!pod?.inviteCode) {
      showNotification('Invite code not available yet.', 'warning');
      return;
    }

    try {
      await navigator.clipboard.writeText(pod.inviteCode);
      showNotification('Invite code copied.', 'success');
    } catch (error) {
      console.warn('Failed to copy invite code:', error);
      showNotification('Failed to copy invite code.', 'error');
    }
  };

  const handleJoinPodByCode = async () => {
    if (!inviteCodeInput.trim()) {
      showNotification('Enter an invite code to join.', 'warning');
      return;
    }

    try {
      const response = await fetch(getFunctionUrl('joinPodByCode'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          inviteCode: inviteCodeInput.trim(),
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to join the project');
      }

      setInviteCodeInput('');
      showNotification('Joined the project successfully.', 'success');
    } catch (error) {
      console.error('Join pod failed:', error);
      showNotification(error.message || 'Failed to join the project', 'error');
    }
  };

  // Redeem a coupon code (e.g. DESIGN30, SANDBOX). Backend's applyCoupon()
  // either stores a pendingDiscount on the user doc (recurring discount
  // coupons → picked up by next Stripe checkout) or grants prepaid credits
  // directly to the pod (one-shot credit coupons like SANDBOX).
  //
  // Accepts an optional `codeArg` so callers (e.g. URL-driven auto-redeem)
  // can pass a code directly without first writing it to state. When
  // codeArg is omitted, falls back to the `couponCode` state (manual entry).
  // Returns the result object so callers can decide what to do (e.g. show
  // a different toast for auto-redemption).
  const handleRedeemCoupon = async (codeArg = null) => {
    const raw = (codeArg ?? couponCode ?? '').toString();
    const normalized = raw.trim().toUpperCase();
    if (!normalized) {
      setCouponResult({ success: false, message: 'Enter a coupon code.' });
      return { success: false };
    }
    if (!currentUser) {
      showNotification('Please sign in to continue.', 'warning');
      return { success: false, needsAuth: true };
    }
    if (couponLoading) return { success: false, alreadyLoading: true };

    try {
      setCouponLoading(true);
      setCouponResult(null);

      // forceProduction:true so localhost-frontend dev (and the in-PowerPoint
      // plugin loaded from localhost via Office Dialog) hit the deployed
      // Cloud Function. The emulator can't validate live Firebase ID tokens
      // anyway, and browsers block mixed-content https → http requests.
      const response = await fetch(buildFunctionUrl('redeemCoupon', { forceProduction: true }), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({ couponCode: normalized }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.success === false) {
        setCouponResult({
          success: false,
          message: payload?.error || 'Invalid coupon code.',
        });
        return { success: false, error: payload?.error };
      }

      // applyCoupon response: { success: true, coupon: { type, value, message, creditCount? } }
      const successMessage = payload?.coupon?.message || 'Coupon applied.';
      setCouponResult({ success: true, message: successMessage });
      setCouponCode('');
      return { success: true, message: successMessage, coupon: payload?.coupon };
    } catch (error) {
      console.error('Coupon redemption error:', error);
      setCouponResult({
        success: false,
        message: 'Something went wrong. Please try again.',
      });
      return { success: false, error: error.message };
    } finally {
      setCouponLoading(false);
    }
  };

  // ─── URL ?code= auto-redemption ──────────────────────────────────────
  // Sales sends a personalized follow-up link: re-stack.com/settings?code=SANDBOX
  // We want that to redeem itself with zero typing on the prospect's side:
  //   1. If signed in: redeem immediately, show success toast, clear URL.
  //   2. If NOT signed in: stash code in sessionStorage so it survives the
  //      sign-in redirect (which lands them back on /settings), then the
  //      same effect fires again with isAuthenticated=true and finishes
  //      the redemption.
  //   3. Once redeemed (success OR known-fatal failure like "already used"),
  //      clear sessionStorage so a refresh doesn't re-trigger.
  //
  // pendingCodeRef prevents double-fire within a single session
  // (effect deps would otherwise re-trigger if the parent re-renders).
  // We deliberately omit handleRedeemCoupon from the deps array — it isn't
  // memoized and stays referentially-stable enough for this use because
  // the ref-guard short-circuits any re-attempt with the same code.
  const pendingCodeRef = useRef(null);
  const handleRedeemCouponRef = useRef(null);
  handleRedeemCouponRef.current = handleRedeemCoupon;

  useEffect(() => {
    const codeFromUrl = (searchParams.get('code') || '').trim().toUpperCase();
    const codeFromStorage = (
      typeof window !== 'undefined'
        ? sessionStorage.getItem('pending_coupon_code') || ''
        : ''
    ).trim().toUpperCase();
    const pendingCode = codeFromUrl || codeFromStorage;
    if (!pendingCode) return;

    // Always stash the URL-provided code so it survives sign-in redirects;
    // then strip ?code from the URL so a refresh doesn't keep re-firing
    // (sessionStorage is the source of truth from this point on).
    if (codeFromUrl) {
      try { sessionStorage.setItem('pending_coupon_code', codeFromUrl); } catch (_) { /* private mode */ }
      const next = new URLSearchParams(searchParams);
      next.delete('code');
      setSearchParams(next, { replace: true });
    }

    // Not signed in yet — the sign-in flow lands users back on this route,
    // at which point this effect will re-fire with isAuthenticated=true
    // and the sessionStorage code will still be there.
    if (!isAuthenticated || !currentUser) return;

    // Already attempted this code in this session? Skip.
    if (pendingCodeRef.current === pendingCode) return;
    pendingCodeRef.current = pendingCode;

    (async () => {
      try {
        const result = await (handleRedeemCouponRef.current?.(pendingCode) || Promise.resolve({}));
        if (result.success) {
          showNotification(result.message || 'Sandbox credits added to your account.', 'success');
        } else if (result.error) {
          // Surface backend errors but DON'T re-prompt — if they've already
          // redeemed (oneTimeUse: true) or the code is invalid, no point
          // showing the same toast on every page visit.
          showNotification(result.error, 'info');
        }
      } finally {
        try { sessionStorage.removeItem('pending_coupon_code'); } catch (_) { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser, searchParams]);

  // Simple plan display names - VERSION 1.0
  const getPlanDisplayName = (plan) => {
    const key = normalizePlanKey(plan);
    return PLAN_CONFIGS[key]?.name || 'Free';
  };

  // Current purchased seat count (Stripe item quantity).
  const currentSeats = Math.max(
    0,
    Math.trunc(Number(subscriptionDetails?.seats || subscriptionDetails?.seatLimit) || 0)
  );

  // Get current monthly price for display = seats x $800.
  const getCurrentPlanPrice = () => {
    const key = normalizePlanKey(subscriptionPlan);
    if (key === 'pro') {
      const seats = currentSeats || 1;
      return `$${(seats * PRICE_PER_SEAT).toLocaleString()}`;
    }
    return '-';
  };

  return {
    // Auth & Navigation
    currentUser,
    signOut,
    isAuthenticated,
    handleBack,

    // Subscription Data
    subscriptionDetails,
    subscriptionPlan,
    isLoadingSubscription,
    isSubscriptionActive,
    isSubscriptionCancelled,

    // Usage Data
    usageSummary,
    shouldSuggestCreditTopUp,
    hasOverage,

    // UI State
    isProcessingPayment,
    buyingBoosterPack,
    isPodLoading,
    isCancellingSubscription,

    // Dialog State
    deleteDialogOpen,
    setDeleteDialogOpen,
    confirmDeleteText,
    setConfirmDeleteText,
    isDeletingAccount,
    deleteAccountGuidance,
    setDeleteAccountGuidance,
    stripeRedirect,

    // Notification State
    notification,
    handleCloseNotification,

    // Handlers
    handleDeleteAccount,
    handleDeleteOrganization,
    deleteOrgDialogOpen,
    setDeleteOrgDialogOpen,
    deleteOrgConfirmOpen,
    setDeleteOrgConfirmOpen,
    confirmDeleteOrgText,
    setConfirmDeleteOrgText,
    isDeletingOrg,
    deleteOrgError,
    setDeleteOrgError,
    handleSubscribe,
    handleBuyBoosterPack,
    handleManageSubscription,
    handleCancelSubscriptionNow,
    showNotification,
    handleInviteMember,
    handleRemoveMember,
    handleCancelPendingInvite,
    cancellingInviteFor,
    seatChangePreview,
    loadingSeatChangePreview,
    confirmingSeatChange,
    requestSeatChange,
    cancelSeatChange,
    performSeatChange,
    handleCopyInviteLink,
    handleJoinPodByCode,
    handleRedeemCoupon,

    // Helper Functions
    getPlanDisplayName,
    getCurrentPlanPrice,

    // Seat model
    currentSeats,

    // Constants
    PLAN_CONFIGS,
    BOOSTER_PACKS,
    PRICE_PER_SEAT,
    CREDITS_PER_SEAT,
    MIN_SEATS,
    MAX_SEATS,

    // Pod Data
    pod,
    podId,
    podRole,
    hasPod,
    isAdmin,
    isMember,
    podMembers,
    isLoadingMembers,
    inviteEmail,
    setInviteEmail,
    inviteCodeInput,
    setInviteCodeInput,
    podNameInput,
    setPodNameInput,
    projectCodeInput,
    setProjectCodeInput,
    saveProjectCode,
    pendingInvites,
    pendingInvitesLoading,
    acceptingInviteFor,
    decliningInviteFor,
    handleAcceptPendingInvite,
    handleDeclinePendingInvite,
    refreshPendingInvites,

    // Org seat join (license layer)
    orgJoinOptions,
    joiningOrgId,
    orgJoinNeedsVerification,
    resendingVerification,
    handleJoinOrg,
    handleResendVerification,

    // Coupon redemption
    couponCode,
    setCouponCode,
    couponLoading,
    couponResult,
    setCouponResult,
  };
};

export { useSubscription };
export default useSettingsScreen;
